import { Injectable } from '@angular/core';

import { Observable, combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';

import { Select, Store } from '@ngxs/store';

import { isNil } from 'lodash';

import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';

import { SetChapterCurrentLayersIndexPosition } from 'src/app/story/state/chapters/story.chapters.actions';
import { NodeActions } from 'src/app/engine/state/nodes/engine.nodes.actions';
import { EngineNodesState } from '../engine/state/nodes/engine.nodes.state';
import { VisibilityLayerModel } from './models/visibility-layers.model';
import { ColorRecipeModel } from '../edit/models/color-recipe.model';
import { HumanInterface } from '../engine/models/human.interface';

declare const Human: HumanInterface;
declare const ScalarFresnel: any;

@Injectable({
  providedIn: 'root',
})
export class VisibilityLayersService {
  @Select(StoryChaptersState.getSelectedChapterCurrentLayers)
  selectedChapterCurrentLayers$: Observable<any>;

  @Select(StoryChaptersState.getSelectedChapterCurrentLayersIndex)
  selectedChapterCurrentLayersIndex$: Observable<any>;

  @Select(StoryChaptersState.getSelectedChapterCurrentLayersPosition)
  selectedChapterCurrentLayersPosition$: Observable<any>;

  @Select(StoryChaptersState.getSelectedChapterId)
  selectedChapterId$: Observable<any>;

  private currentLayers = [];

  private nodesLayerIndex = 0;
  private objectsLayerIndex = 0;

  private selectedChapterId: string;

  // Store initial chapter opacities for nodes so that we don't clobber them
  // When synchronizing / restoring layers.
  private nodeChapterOpacities = {};

  constructor(private store: Store) {}

  initObservables() {
    this.selectedChapterId$
      .pipe(
        filter((selectedChapterId) => {
          return !isNil(selectedChapterId);
        }),
      )
      .subscribe((selectedChapterId) => {
        this.selectedChapterId = selectedChapterId;
        this.updateNodeChapterOpacities();
      });

    this.selectedChapterCurrentLayers$.subscribe((currentLayers) => {
      this.currentLayers = currentLayers;

      this.nodesLayerIndex = this.objectsLayerIndex = this.store.selectSnapshot(
        StoryChaptersState.getSelectedChapterCurrentLayersIndex,
      );
    });

    combineLatest([
      this.selectedChapterCurrentLayersIndex$,
      this.selectedChapterCurrentLayersPosition$,
    ])
      .pipe(
        filter(([currentIndex, currentPosition]) => {
          return !isNil(currentIndex) && !isNil(currentPosition);
        }),
      )
      .subscribe(([currentIndex, currentPosition]) => {
        this.synchronizeScene(currentIndex, currentPosition, true);
      });
  }

  // Update story chapter state and add to undo stack
  recordLayerIndexPosition(index: number, position: number) {
    this.store.dispatch(
      new SetChapterCurrentLayersIndexPosition(this.selectedChapterId, index, position, true),
    );
  }

  // Synch the scene either through the app's nodes model
  // Or directly via the engine.
  synchronizeScene(index: number, position: number, nodesMode: boolean) {
    this.updateLayerIndex(index, this.currentLayers, nodesMode);
    this.updateLayerPosition(position, this.currentLayers, nodesMode);
  }

  // Fraction calculation methods exclude the last layer
  fractionToIndex(fraction: number): number {
    const threshold = this.getThreshold();
    const index = Math.floor(fraction / threshold);
    return Math.min(this.currentLayers.length - 2, index);
  }

  fractionToPosition(fraction: number, index: number): number {
    const threshold = this.getThreshold();
    const position = (fraction - threshold * index) / threshold;
    return Math.max(0, Math.min(position, 1));
  }

  indexPositionToFraction(index: number, position: number): number {
    const threshold = this.getThreshold();
    return index * threshold + position * threshold;
  }

  // Excludes the last layer
  private getThreshold() {
    return this.currentLayers.length > 1 ? 1 / (this.currentLayers.length - 1) : 1;
  }

  private getCurrentLayerIndexProp(nodesMode: boolean): string {
    return nodesMode ? 'nodesLayerIndex' : 'objectsLayerIndex';
  }

  private updateLayerIndex(
    newIndex: number,
    layersData: VisibilityLayerModel[],
    nodesMode: boolean,
  ) {
    const currentIndexProp = this.getCurrentLayerIndexProp(nodesMode);

    if (newIndex === this[currentIndexProp]) {
      return;
    }

    this.restoreLayers(newIndex, this[currentIndexProp], layersData, nodesMode);

    if (nodesMode) {
      this.nodesLayerIndex = newIndex;
    }

    this.objectsLayerIndex = newIndex; // Always synch objectsLayerIndex
  }

  private updateLayerPosition(
    newPosition: number,
    layersData: VisibilityLayerModel[],
    nodesMode: boolean,
  ) {
    const currentIndexProp = this.getCurrentLayerIndexProp(nodesMode);
    const objectIds = layersData[this[currentIndexProp]].objectIds;
    const opacity = 1 - newPosition;

    this.setObjectsOpacity(objectIds, opacity);
  }

  private restoreLayers(
    newIndex: number,
    oldIndex: number,
    layersData: VisibilityLayerModel[],
    nodesMode: boolean,
  ) {
    const diff = Math.abs(newIndex - oldIndex);
    const dir = (newIndex - oldIndex) / diff;
    let objectIds = [];

    let i = oldIndex;

    while (i !== newIndex) {
      objectIds = objectIds.concat(layersData[i].objectIds);
      i += dir;
    }

    // Update old / jumped layer(s)
    const opacity = dir === 1 ? 0 : 1;
    const shown = dir === -1;

    this.setObjectsOpacity(objectIds, opacity);

    if (nodesMode) {
      this.setNodesShown(objectIds, shown);
    } else {
      this.setObjectsShown(objectIds, shown);
    }

    // Always show current layer when moving right to left.
    if (dir === -1) {
      if (nodesMode) {
        this.setNodesShown(layersData[newIndex].objectIds, true);
      } else {
        this.setObjectsShown(layersData[newIndex].objectIds, true);
      }
    }
  }

  private setObjectsOpacity(objectIds: string[], opacity: number) {
    for (let i = 0; i < objectIds.length; i++) {
      const objectId = objectIds[i];
      const nodeChapterOpacity = this.nodeChapterOpacities[objectId];

      let newOpacity;

      if (this.isFresnelOpacity(nodeChapterOpacity)) {
        newOpacity = nodeChapterOpacity.slice();
        newOpacity[0] = opacity * nodeChapterOpacity[0];
        newOpacity[1] = opacity * nodeChapterOpacity[1];
      } else {
        newOpacity = opacity * nodeChapterOpacity;
      }

      Human.scene.objects[objectId].setOpacity(newOpacity);
    }
  }

  private updateNodeChapterOpacities() {
    const allNodeState = this.store.selectSnapshot(EngineNodesState.getAllNodeState);
    const selectedChapterNodeState = allNodeState[this.selectedChapterId];

    this.nodeChapterOpacities = {};

    Object.entries(selectedChapterNodeState).forEach(([objectId, chapterNodeState]) => {
      this.nodeChapterOpacities[objectId] = 1; // Default to full

      if (chapterNodeState.paint && chapterNodeState.paint.hasOwnProperty('opacity')) {
        const engineColorRecipe = ColorRecipeModel.toEngine(chapterNodeState.paint);

        if (
          this.isFresnelOpacity(engineColorRecipe.opacity) ||
          this.isScalarOpacity(engineColorRecipe.opacity)
        ) {
          this.nodeChapterOpacities[objectId] = engineColorRecipe.opacity;
        }
      }
    });
  }

  private isScalarOpacity(opacity) {
    return typeof opacity === 'number';
  }

  private isFresnelOpacity(opacity) {
    return Array.isArray(opacity);
  }

  private setObjectsShown(objectIds: string[], shown: boolean) {
    const update = {};

    for (let i = 0; i < objectIds.length; i++) {
      update[objectIds[i]] = shown;
    }

    Human.scene.setEnabledObjects({ objects: update });
  }

  private setNodesShown(objectIds, shown) {
    this.store.dispatch(new NodeActions.UpdateNodesShown(shown, objectIds, undefined, false));
  }
}
